"use client";
import ScreenLayout from "@/components/layout/screen-layout";
import ServicePageMeta from "@/components/common/seo/service-page-meta";

export default function PrivacyPolicy() {
    return (
        <>
            <ServicePageMeta
                title="Privacy Policy | Devkrest Data Protection & Privacy Practices"
                description="Learn about Devkrest's privacy policy and how we protect your personal information. We are committed to data protection, secure data handling, and transparent privacy practices. Read our complete privacy policy to understand your rights and our data collection practices."
                keywords={[
                    "privacy policy",
                    "data protection",
                    "personal information",
                    "data privacy",
                    "information security",
                    "data collection",
                    "cookie policy",
                    "data handling",
                    "privacy practices",
                    "data rights",
                    "information protection",
                    "Devkrest privacy",
                    "data security"
                ]}
                serviceType="Privacy Policy"
                url="/privacy-policy"
                image="/images/privacy/privacy-policy-hero.webp"
            />
            <main className="min-h-screen bg-white">
                {/* Hero Section */}
                <div className="relative pt-40 md:pt-44 lg:pt-48 pb-20 overflow-hidden bg-gradient-to-br from-white via-purple-50/30 to-white">
                    {/* Background Elements */}
                    <div className="absolute inset-0 w-full h-full">
                        <div className="absolute inset-0 bg-[linear-gradient(rgba(82,49,231,0.02)_1.6px,transparent_1.6px),linear-gradient(to_right,rgba(82,49,231,0.02)_1.6px,transparent_1.6px)] bg-[size:24px_24px]" />
                        <div className="absolute top-20 left-10 w-20 h-20 bg-gradient-to-r from-purple-400/20 to-blue-400/20 rounded-full blur-xl opacity-50" />
                        <div className="absolute bottom-20 right-10 w-32 h-32 bg-gradient-to-r from-blue-400/20 to-purple-400/20 rounded-full blur-xl opacity-50" />
                    </div>

                    {/* Content Container */}
                    <div className="relative z-10">
                        <ScreenLayout>
                            <div className="max-w-4xl mx-auto">
                                <div className="space-y-6 text-center">
                                    {/* Main Heading */}
                                    <div className="space-y-4">
                                        <h1 className="text-4xl sm:text-5xl md:text-6xl font-semibold leading-[1.1]">
                                            <span className="text-gray-900">Privacy</span>{" "}
                                            <span className="bg-gradient-to-r from-[#5231E7] to-[#764AF1] bg-clip-text text-transparent">
                                                Policy
                                            </span>
                                        </h1>
                                    </div>

                                    {/* Subtitle */}
                                    <div className="space-y-4">
                                        <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
                                            We, at Devkrest, are committed to protecting the privacy of our visitors.
                                            We do not spam, sell, rent, lease or give away the information to any third party/parties.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </ScreenLayout>
                    </div>
                </div>

                {/* Content Section */}
                <div className="py-12 md:py-16">
                    <ScreenLayout>
                        <div className="max-w-4xl mx-auto">
                            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 md:p-12">
                                {/* Introduction */}
                                <div className="prose prose-lg max-w-none">
                                    <p className="text-gray-700 leading-relaxed mb-8">
                                        It is projected to describe how we use and process your personal information. Please ensure that by visiting and using devkrest.com, you are agreeing and accepting the policies that are described in this Privacy Policy.
                                    </p>

                                    <p className="text-gray-700 leading-relaxed mb-8">
                                        This Privacy Policy also explains how we are going to use your personal information and what happens to any personal data that you provide to us. Moreover, we do update the policies of this page from time to time, so please keep reviewing the page on a regular basis.
                                    </p>

                                    {/* Section 1: Information We Collect */}
                                    <div className="mb-12">
                                        <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6 flex items-center">
                                            <span className="w-8 h-8 bg-gradient-to-r from-[#5231E7] to-[#764AF1] text-white rounded-full flex items-center justify-center text-sm font-bold mr-4">
                                                1
                                            </span>
                                            Information We Collect
                                        </h2>

                                        <div className="space-y-6">
                                            <div>
                                                <h3 className="text-xl font-semibold text-gray-900 mb-4">(i) Personal Information</h3>
                                                <p className="text-gray-700 leading-relaxed mb-4">
                                                    The personal information that we may collect include name, contact information, IP addresses, product and service selections and other information, identifying you. By providing your mobile number on our website, you agree to receive communications by text messages and voice calls about your inquiry. You may opt-out by replying STOP or ask for more information by replying HELP. Message frequency varies. Message and data rates may apply.
                                                </p>
                                                <p className="text-gray-700 leading-relaxed mb-4">
                                                    We value your privacy and want to assure you that we do not sell, share, or distribute your mobile number to third-party companies. Your information is used solely for our own customer support and service-related communications. All the above categories exclude text messaging originator opt-in data and consent; this information will not be shared with any third parties.
                                                </p>
                                                <p className="text-gray-700 leading-relaxed mb-4">
                                                    We may also gather and collect personal information from you at different points, including but not limited to the following:
                                                </p>
                                                <ul className="list-disc list-inside text-gray-700 leading-relaxed space-y-2 ml-4">
                                                    <li>At the time of communicating with you as a customer or prospective customer</li>
                                                    <li>When you prefer to visit our website or make use of the platform</li>
                                                    <li>While registering you as an end-user of our products or services and one account is especially made for you</li>
                                                    <li>When you fill our "Contact Us" "Let's talk" forms to get in touch with our experts</li>
                                                </ul>
                                            </div>

                                            <div>
                                                <h3 className="text-xl font-semibold text-gray-900 mb-4">(ii) Non-personal Data</h3>
                                                <p className="text-gray-700 leading-relaxed">
                                                    We may also collect or gather non-personal data or information about users whenever they prefer to interact with our website. Non-personal data and information include the browser name that user access, the type of computer, technical information like operating system and the Internet service providers and other similar information via Google Analytics.
                                                </p>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Section 2: Cookies */}
                                    <div className="mb-12">
                                        <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6 flex items-center">
                                            <span className="w-8 h-8 bg-gradient-to-r from-[#5231E7] to-[#764AF1] text-white rounded-full flex items-center justify-center text-sm font-bold mr-4">
                                                2
                                            </span>
                                            Cookies
                                        </h2>
                                        <p className="text-gray-700 leading-relaxed">
                                            We use cookies to help us provide you with a better website experience. Cookies are small text files that are stored on your computer or mobile device when you visit our website. They help us remember your preferences and provide you with a more personalized experience.
                                        </p>
                                    </div>
                                </div>

                                <nav
                                    className="mt-8 border-t border-gray-100 pt-8"
                                    aria-label="Related legal pages"
                                >
                                    <p className="mb-3 text-sm font-medium text-gray-900">
                                        Related legal pages
                                    </p>
                                    <div className="flex flex-wrap gap-4 text-sm">
                                        <a
                                            href="/terms-and-conditions"
                                            className="font-medium text-[#5231E7] hover:underline"
                                        >
                                            Terms & Conditions
                                        </a>
                                        <a
                                            href="/refund-cancellation-policy"
                                            className="font-medium text-[#5231E7] hover:underline"
                                        >
                                            Refund & Cancellation Policy
                                        </a>
                                    </div>
                                </nav>
                            </div>
                        </div>
                    </ScreenLayout>
                </div>
            </main>
        </>
    );
} 